import React, { ReactElement, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import styles from './app-header.module.scss';

import { useUserData } from 'context';
import { ROLE_NAME, STATUS_NAME } from 'enums';
import { AnchorElement, PATHS } from 'types';
import {
  Button,
  Container,
  Divider,
  LinearProgress,
  Logo,
  MainMenu,
  Navigation,
  NavigationMenu,
  Util,
} from 'views/components';
import { OrganizationInfo } from 'views/components/AppHeader/OrganizationInfo';
import { UserInfo } from 'views/components/AppHeader/UserInfo';

const MAX_SCREEN_WIDTH = 800;
const NAVIGATION_COLLISION_GAP = 16;
const HALF_DIVISOR = 2;

const AppHeader = (): ReactElement => {
  const {
    user,
    currentOrganization,
    isGlobalLoading,
    isShowCompleteProfileButton,
    billingStatus,
    frozenOrganization,
  } = useUserData();

  const intl = useIntl();
  const [mainMenuAnchorEl, setMainMenuAnchorEl] = useState<AnchorElement>(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isNavigationCentered, setIsNavigationCentered] = useState(true);

  const headerInnerRef = useRef<HTMLDivElement>(null);
  const headerBrandRef = useRef<HTMLDivElement>(null);
  const headerNavRef = useRef<HTMLDivElement>(null);
  const headerUserBlockRef = useRef<HTMLDivElement>(null);

  const onMainMenuClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    setMainMenuAnchorEl(e.currentTarget);
  };
  const closeMainMenu = (): void => setMainMenuAnchorEl(null);
  const isShowLogo =
    !currentOrganization ||
    user?.requestStatus === STATUS_NAME.PENDING ||
    (currentOrganization && user?.role.name === ROLE_NAME.UNAPPROVED);
  const isOrganizationFrozen =
    Boolean(frozenOrganization) || billingStatus?.accessStatus === 'FROZEN';
  const isHeaderNavigationVisible = !isOrganizationFrozen;

  useEffect(() => {
    function handleResize(): void {
      setWindowWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (windowWidth > MAX_SCREEN_WIDTH || !isHeaderNavigationVisible) {
      setMainMenuAnchorEl(null);
    }
  }, [windowWidth, isHeaderNavigationVisible]);

  useEffect(() => {
    if (isShowLogo || !isHeaderNavigationVisible) {
      return undefined;
    }

    const headerInner = headerInnerRef.current;
    const headerBrand = headerBrandRef.current;
    const headerNav = headerNavRef.current;
    const headerUserBlock = headerUserBlockRef.current;

    if (!headerInner || !headerBrand || !headerNav || !headerUserBlock) {
      return undefined;
    }

    let animationFrameId: number | null = null;

    const updateNavigationPlacement = (): void => {
      const headerWidth = headerInner.offsetWidth;
      const brandWidth = headerBrand.offsetWidth;
      const navWidth = headerNav.offsetWidth;
      const userBlockWidth = headerUserBlock.offsetWidth;
      const centeredNavLeft = (headerWidth - navWidth) / HALF_DIVISOR;
      const centeredNavRight = centeredNavLeft + navWidth;
      const isEnoughSpaceOnLeft =
        centeredNavLeft >= brandWidth + NAVIGATION_COLLISION_GAP;
      const isEnoughSpaceOnRight =
        centeredNavRight <= headerWidth - userBlockWidth - NAVIGATION_COLLISION_GAP;
      const canCenterNavigation = isEnoughSpaceOnLeft && isEnoughSpaceOnRight;

      setIsNavigationCentered(prevState =>
        prevState === canCenterNavigation ? prevState : canCenterNavigation,
      );
    };

    const scheduleNavigationPlacementUpdate = (): void => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }

      animationFrameId = window.requestAnimationFrame(updateNavigationPlacement);
    };

    scheduleNavigationPlacementUpdate();

    if (!window.ResizeObserver) {
      window.addEventListener('resize', scheduleNavigationPlacementUpdate);

      return () => {
        if (animationFrameId !== null) {
          window.cancelAnimationFrame(animationFrameId);
        }
        window.removeEventListener('resize', scheduleNavigationPlacementUpdate);
      };
    }

    const resizeObserver = new ResizeObserver(scheduleNavigationPlacementUpdate);
    [headerInner, headerBrand, headerNav, headerUserBlock].forEach(element =>
      resizeObserver.observe(element),
    );
    window.addEventListener('resize', scheduleNavigationPlacementUpdate);

    return () => {
      resizeObserver.disconnect();
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener('resize', scheduleNavigationPlacementUpdate);
    };
  }, [
    isShowLogo,
    isHeaderNavigationVisible,
    currentOrganization?.name,
    user?.photoUrl,
    isShowCompleteProfileButton,
  ]);

  const headerInnerClassNames = classNames(styles.header__inner, {
    [styles['header__inner--centered-nav']]:
      !isShowLogo && isHeaderNavigationVisible && isNavigationCentered,
    [styles['header__inner--inline-nav']]:
      !isShowLogo && isHeaderNavigationVisible && !isNavigationCentered,
  });

  return (
    <header className={styles.header}>
      <Container className={styles.header__container}>
        <div ref={headerInnerRef} className={headerInnerClassNames}>
          {isShowLogo ? (
            <NavLink className={styles.header__logo} to={PATHS.ROOT}>
              <Logo height={32} />
            </NavLink>
          ) : (
            <>
              <div ref={headerBrandRef} className={styles['header__brand-block']}>
                <NavLink
                  className={`${styles.header__logo} ${styles['header__logo--with-organization']}`}
                  to={PATHS.ROOT}
                >
                  <Logo height={32} />
                </NavLink>
                <div className={styles.divider}>
                  <Divider vertical />
                </div>
                {isHeaderNavigationVisible && (
                  <div className={styles.header__menu}>
                    <Util paddingRight="16px">
                      <Button
                        mode="tertiary"
                        color="dark"
                        icon={<MainMenu color="currentColor" />}
                        onClick={onMainMenuClick}
                        aria-label={intl.formatMessage({ id: 'header.main-menu.button' })}
                      />
                    </Util>
                    {mainMenuAnchorEl && (
                      <NavigationMenu
                        anchorEl={mainMenuAnchorEl}
                        handleClose={closeMainMenu}
                      />
                    )}
                    <Divider vertical />
                  </div>
                )}
                <OrganizationInfo organization={currentOrganization} />
                {isHeaderNavigationVisible && (
                  <div
                    className={`${styles.divider} ${styles['header__inline-nav-divider']}`}
                  >
                    <Divider vertical />
                  </div>
                )}
              </div>
              {isHeaderNavigationVisible && (
                <div ref={headerNavRef} className={styles.header__nav}>
                  <Navigation />
                </div>
              )}
            </>
          )}
          <div ref={headerUserBlockRef} className={styles['header__user-block']}>
            {isShowCompleteProfileButton &&
              user?.role.name === ROLE_NAME.MENTEE &&
              !user?.expertise && (
                <Button link={PATHS.ROOT} mode="secondary" size="small" color="accent">
                  <FormattedMessage id="header.complete-profile.button" />
                </Button>
              )}
            <div className={styles['header__user-info']}>
              <UserInfo imageUrl={user?.photoUrl || ''} />
            </div>
          </div>
        </div>
      </Container>
      {isGlobalLoading && <LinearProgress />}
    </header>
  );
};

export { AppHeader };
